import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/providers/language-provider";
import { useTheme } from "@/providers/theme-provider";
import { ThemeToggle } from "@/features/theme-toggle";
import { LanguageToggle } from "@/features/language-toggle";
import { Input } from "@/components/ui/input";
import { getData, setData } from "@/lib/indexed-db";
import { useAuth } from "@/providers/auth-provider";
import { useChats } from "@/providers/chat-provider";
import { ChatColorPicker } from "@/components/blocks/chat-color-picker";
import { ButtonSubmit } from "@/components/blocks/button-submit";

interface DialogSettingsHandles {
  open: () => void;
  close: () => void;
}

interface DialogSettingsProps {
  children: React.ReactNode;
}

const baseBgColor = "#2563EB";

export const DialogSettings = forwardRef<
  DialogSettingsHandles,
  DialogSettingsProps
>(({ children }, ref) => {
  const { t, language, setLanguage } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user, updateUserDataField } = useAuth();
  const { fetchChats } = useChats();
  const [isOpen, setIsOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(theme);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [coupon, setCoupon] = useState("");
  const [bgColor, setBgColor] = useState(user?.data?.bgColor ?? baseBgColor);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => handleOpenChange(true),
    close: () => handleOpenChange(false),
  }));

  const onClickSave = () => handleOpenChange(false, true);

  const handleSaveData = async () => {
    try {
      setIsSubmitting(true);
      const bgColorIsChanged = bgColor !== user?.data?.bgColor;
      if (bgColorIsChanged) {
        await updateUserDataField({
          newData: {
            bgColor: bgColor,
          },
        });
        await fetchChats();
      }
    } catch (error) {
      console.error("Error saving settings:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = async (isOpen: boolean, save: boolean = false) => {
    if (isOpen) {
      // 창이 열리면 현재 상태를 설정.
      setCurrentTheme(theme);
      setCurrentLanguage(language);
    } else {
      if (save) {
        await handleSaveData();
      } else {
        // 저장 전으로 되돌린다.
        setTheme(currentTheme);
        setLanguage(currentLanguage);
        setBgColor(user?.data?.bgColor ?? baseBgColor);
      }

      setCoupon("");
    }
    setIsOpen(isOpen);
  };

  const handleCoupon = async () => {
    let used = false;
    if (coupon === "PROJECT-NAME-IS-KORIMY-TALK") {
      const result = await getData("settings", user._id);
      await setData("settings", user._id, {
        ...result?.data,
        auth: "FAMILY",
      });
      used = true;
      console.log("you are authorized.");
    }
    if (used) {
      setCoupon("");
    }
  };

  const handleTest = async () => {
    const userData = user.data;
    console.log("handleTest", userData);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(isOpen: boolean) => handleOpenChange(isOpen)}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("Settings")}</DialogTitle>
          <DialogDescription>{t("SettingsDesc")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="theme" className="text-right">
              {t("Theme")}
            </Label>
            <ThemeToggle />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="username" className="text-right">
              {t("Language")}
            </Label>
            <LanguageToggle />
          </div>
          <div className="grid grid-cols-4 gap-4">
            <Label htmlFor="username" className="text-right mt-4">
              {t("Color")}
            </Label>
            <div className="col-span-3">
              <ChatColorPicker color={bgColor} onColorChange={setBgColor} />
            </div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="username" className="text-right">
              {t("Coupon")}
            </Label>
            <Input
              className="col-span-2"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
            />
            <Button onClick={handleCoupon}>{t("Use")}</Button>
          </div>
        </div>
        <DialogFooter>
          <div className="flex w-full">
            {import.meta.env.DEV && (
              <Button type="submit" onClick={handleTest}>
                Test
              </Button>
            )}
            {/* <Button className="ml-auto" type="submit" onClick={handleSave}>
              {t("Save")}
            </Button> */}
            <ButtonSubmit
              className="ml-auto"
              type="submit"
              onClick={onClickSave}
              isSubmitting={isSubmitting}
            >
              {t("Save")}
            </ButtonSubmit>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

DialogSettings.displayName = "DialogSettings";
